/**
 * @file CarouselCard.jsx
 * @description Mobil karusel görünümü için kompakt ilan kartı
 *
 * TripAdvisor tarzı yatay kart bileşeni. Solda kare görsel,
 * sağda başlık, konum, fiyat ve özellikler gösterilir.
 * Mobil harita görünümünde karusel içinde kullanılır.
 *
 * @module widget/components/CarouselCard
 */

import { memo } from "react";
import { MapPin } from "lucide-react";
import { Image } from "@openai/apps-sdk-ui/components/Image";
import { formatPrice, COLORS } from "../utils";
import { ListingBadges } from "./ListingBadges";

/**
 * Karusel İlan Kartı Bileşeni
 *
 * Mobil cihazlarda harita altındaki yatay kaydırılabilir
 * listede gösterilen kompakt ilan kartı.
 *
 * @component
 * @param {Object} props - Bileşen prop'ları
 * @param {Object} props.listing - İlan verisi
 * @param {string} [props.listing.title] - İlan başlığı
 * @param {string} [props.listing.location] - Konum bilgisi
 * @param {number} [props.listing.price] - Fiyat
 * @param {string} [props.listing.currency] - Para birimi (TRY, USD, EUR)
 * @param {string} [props.listing.room] - Oda sayısı (örn: "3+1")
 * @param {number} [props.listing.m2] - Metrekare
 * @param {string[]} [props.listing.images] - Görsel URL dizisi
 * @param {string} [props.listing.image] - Tek görsel URL (fallback)
 * @param {boolean} props.isActive - Seçili durum (haritada highlight)
 * @param {Function} props.onClick - Tıklama handler'ı
 * @param {boolean} props.isDark - Karanlık mod aktif mi
 *
 * @returns {JSX.Element} Karusel kart bileşeni
 *
 * @example
 * <CarouselCard
 *   listing={{ title: "3+1 Daire", price: 5000000, room: "3+1" }}
 *   isActive={selectedId === listing.id}
 *   onClick={() => handleSelect(listing.id)}
 *   isDark={false}
 * />
 */
function CarouselCardInner({ listing, isActive, onClick, isDark }) {
  const priceLabel = formatPrice(listing?.price, listing?.currency);
  const imageUrl = listing?.images?.[0] || listing?.image;

  return (
    <div
      className={`
        flex-shrink-0 w-[300px] h-[120px] rounded-2xl overflow-hidden cursor-pointer
        flex flex-row
        transition-all duration-200 ease-out
        ${isActive
          ? "ring-2 ring-offset-2 shadow-xl scale-[1.02]"
          : "shadow-lg hover:shadow-xl"
        }
        ${isDark
          ? "bg-zinc-800 ring-offset-zinc-900"
          : "bg-white ring-offset-white"
        }
      `}
      style={{
        ringColor: isActive ? COLORS.primary : undefined,
        backgroundColor: isDark ? '#27272a' : '#ffffff',
      }}
      onClick={onClick}
    >
      {/* Image - left side (square) */}
      <div className="relative w-[120px] h-full flex-shrink-0 overflow-hidden">
        {imageUrl ? (
          <Image
            src={imageUrl}
            alt={listing?.title || "İlan"}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className={`w-full h-full flex items-center justify-center ${isDark ? "bg-white/5" : "bg-black/5"}`}>
            <span className={`text-xs ${isDark ? "text-white/30" : "text-black/30"}`}>Görsel yok</span>
          </div>
        )}
        {/* Badges overlay */}
        {(listing?.isOpportunity || listing?.priceTrend === "down" || listing?.badges?.length > 0) && (
          <div className="absolute top-1.5 left-1.5">
            <ListingBadges
              badges={listing?.badges}
              isOpportunity={listing?.isOpportunity}
              priceTrend={listing?.priceTrend}
              priceChangeRate={listing?.priceChangeRate}
              size="sm"
              maxBadges={1}
            />
          </div>
        )}
      </div>

      {/* Content - right side */}
      <div className="flex-1 p-3 flex flex-col justify-between min-w-0">
        {/* Title */}
        <div className={`font-semibold text-sm line-clamp-2 leading-tight ${isDark ? "text-white" : "text-gray-900"}`}>
          {listing?.title || "İlan"}
        </div>

        {/* Location */}
        {listing?.location && (
          <div className={`flex items-center gap-1 text-xs ${isDark ? "text-white/50" : "text-gray-500"}`}>
            <MapPin className="h-3 w-3 flex-shrink-0" strokeWidth={1.5} />
            <span className="truncate">{listing.location}</span>
          </div>
        )}

        {/* Price + Properties */}
        <div className="flex items-center justify-between gap-2">
          <div
            className="text-sm font-bold"
            style={{ color: COLORS.primary }}
          >
            {priceLabel || "—"}
          </div>
          <div className={`text-[10px] ${isDark ? "text-white/40" : "text-gray-400"}`}>
            {[listing?.room, listing?.m2 ? `${listing.m2}m²` : null].filter(Boolean).join(" · ")}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Memoized CarouselCard - karusel item'ları için
 * gereksiz re-render'ları önler.
 */
export const CarouselCard = memo(CarouselCardInner);
export default CarouselCard;
